/**
 * @file CreateCompanyDialog.tsx
 * @brief Sheet form for creating a company via `POST /api/admin/companies`.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — FormSheet + GridBlock chrome
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { BadgePoundSterling, Building2, LoaderCircle, MapPin } from "lucide-react";
import { AddressAutocomplete } from "@/components/ui/AddressAutocomplete";
import { Button } from "@/components/ui/Button";
import { CountryPicker } from "@/components/ui/CountryPicker";
import {
  FormFieldGroup,
  FormSheet,
  FormSheetAlert,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { FormSheetTabs, formSheetTabPanel, type FormSheetTab } from "@/components/ui/FormSheetTabs";
import { Input } from "@/components/ui/Input";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { useSystemSettings } from "@/features/billing/api";
import { useCreateCompany } from "../api";
import { ALL_COMPANY_RATE_FIELDS, CURRENCY_CODES } from "../companyRateFields";
import { CreateCompanyInputSchema, type CreateCompanyInput } from "../schemas";
import { CompanyRateFieldsSection } from "./CompanyRateFieldsSection";

interface CreateCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (companyId: string) => void;
}

interface FormValues {
  name: string;
  domain?: string;
  billing_email?: string;

  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;

  phone_number?: string;
  tax_id?: string;

  custom_rate_usd_major?: string;
  custom_rate_eur_major?: string;
  custom_rate_gbp_major?: string;

  [key: `custom_${string}_major`]: string | undefined;
}

function majorToMinor(input?: string): number | undefined {
  if (!input || input.trim() === "") return undefined;
  const n = Number(input);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100);
}

export function CreateCompanyDialog({ open, onOpenChange, onCreated }: CreateCompanyDialogProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const [tab, setTab] = useState<CreateCompanyTab>("profile");
  const createCompany = useCreateCompany();
  const settingsQuery = useSystemSettings();

  const form = useForm<FormValues>({
    defaultValues: {
      name: "",
      domain: "",
      billing_email: "",
      address: "",
      city: "",
      state: "",
      postal_code: "",
      country: "",
      phone_number: "",
      tax_id: "",
      custom_rate_usd_major: "",
      custom_rate_eur_major: "",
      custom_rate_gbp_major: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset();
      setServerError(null);
      setTab("profile");
    }
  }, [open, form]);

  const onSubmit = form.handleSubmit(
    async (values) => {
      setServerError(null);

      const payload: CreateCompanyInput = {
        name: values.name.trim(),
        domain: values.domain?.trim() || undefined,
        billing_email: values.billing_email?.trim() || undefined,
        address: values.address?.trim() || undefined,
        city: values.city?.trim() || undefined,
        state: values.state?.trim() || undefined,
        postal_code: values.postal_code?.trim() || undefined,
        country: values.country?.trim() || undefined,
        phone_number: values.phone_number?.trim() || undefined,
        tax_id: values.tax_id?.trim() || undefined,
      };

      for (const f of ALL_COMPANY_RATE_FIELDS) {
        for (const c of CURRENCY_CODES) {
          const minor = majorToMinor(values[f.majorKey(c)]);
          if (minor !== undefined) payload[f.minorKey(c)] = minor;
        }
      }

      const parsed = CreateCompanyInputSchema.safeParse(payload);
      if (!parsed.success) {
        const first = parsed.error.issues[0];
        setServerError(first?.message ?? "Validation failed");
        return;
      }

      try {
        const created = await createCompany.mutateAsync(parsed.data);
        toast({
          variant: "success",
          title: "Company created",
          description: `${parsed.data.name} has been added.`,
        });
        onOpenChange(false);
        onCreated?.(created.id);
      } catch (err) {
        if (err instanceof ApiClientError) setServerError(err.message);
        else setServerError("Unexpected error. Please try again.");
      }
    },
    // The only required field (name) is on Profile: show it if it failed
    // (DES 01-10-2026: form standard, as Edit company).
    () => setTab("profile"),
  );

  return (
    <FormSheet open={open} onOpenChange={onOpenChange}>
      {/* DES 01-10-2026: form standard. Opens like Cmd+K, as Edit company. */}
      <FormSheetContent side="palette">
        <form
          onSubmit={onSubmit}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          noValidate
        >
          <FormSheetFrame>
            <FormSheetHeader>
              <FormSheetTitle>Create company</FormSheetTitle>
              <FormSheetDescription>
                Onboards a new tenant. Only the name is required. Billing, address and custom rates
                can be filled in later.
              </FormSheetDescription>
            </FormSheetHeader>

            <FormSheetTabs
              tabs={CREATE_COMPANY_TABS}
              value={tab}
              onChange={setTab}
              label="Create company"
            />

            <FormSheetBody>
              {/* DES 01-10-2026: form standard. Same tabs as Edit company
                  (Enterprise only exists once the company does). */}
              <div {...formSheetTabPanel(tab, "profile")}>
                <FormSheetSection
                  hideHeading
                  title="Profile"
                  subtitle="Company identity and contact details."
                >
                  <FormSheetFields>
                    <FormSheetField span={2}>
                      <FormFieldGroup label="Name" required>
                        <Input
                          capitalizeFirst
                          placeholder="Acme Corp."
                          autoFocus
                          {...form.register("name", { required: true })}
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup label="Domain">
                        <Input placeholder="acme.com" {...form.register("domain")} />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup label="Billing email">
                        <Input
                          type="email"
                          placeholder="ap@acme.com"
                          {...form.register("billing_email")}
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup label="Phone">
                        <Input placeholder="+44 …" {...form.register("phone_number")} />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup label="Tax / VAT ID">
                        <Input autoCapitalize="characters" placeholder="GB123456789" {...form.register("tax_id")} />
                      </FormFieldGroup>
                    </FormSheetField>
                  </FormSheetFields>
                </FormSheetSection>
              </div>

              <div {...formSheetTabPanel(tab, "address")}>
                <FormSheetSection
                  hideHeading
                  title="Address"
                  subtitle="Registered address for invoices and compliance."
                >
                  <FormSheetFields>
                    <FormSheetField span={2}>
                      <AddressAutocomplete
                        id="create-company-address"
                        label="Street"
                        value={form.watch("address") || ""}
                        onChange={(street) =>
                          form.setValue("address", street, {
                            shouldDirty: true,
                          })
                        }
                        onSelect={(parts) => {
                          form.setValue("address", parts.address, {
                            shouldDirty: true,
                          });
                          if (parts.city) {
                            form.setValue("city", parts.city, {
                              shouldDirty: true,
                            });
                          }
                          if (parts.state) {
                            form.setValue("state", parts.state, {
                              shouldDirty: true,
                            });
                          }
                          if (parts.postal_code) {
                            form.setValue("postal_code", parts.postal_code, {
                              shouldDirty: true,
                            });
                          }
                          if (parts.country) {
                            form.setValue("country", parts.country, {
                              shouldDirty: true,
                            });
                          }
                        }}
                      />
                    </FormSheetField>
                  </FormSheetFields>
                  <FormSheetFields className="sm:grid-cols-3">
                    <FormSheetField>
                      <FormFieldGroup label="City">
                        <Input capitalizeFirst {...form.register("city")} />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup label="State / region">
                        <Input capitalizeFirst {...form.register("state")} />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup label="Postal code">
                        <Input autoCapitalize="characters" {...form.register("postal_code")} />
                      </FormFieldGroup>
                    </FormSheetField>
                    {/* A third of the row in the same grid, as Edit company
                      (DES 01-10-2026: form standard). */}
                    <FormSheetField>
                      <FormFieldGroup label="Country">
                        <CountryPicker
                          value={form.watch("country") || null}
                          onChange={(name) => form.setValue("country", name ?? "")}
                          sheetSubtitle="Registered address country for this company."
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                  </FormSheetFields>
                </FormSheetSection>
              </div>

              <div {...formSheetTabPanel(tab, "rates")}>
                <FormSheetSection
                  hideHeading
                  title="Custom rates (PAYG)"
                  subtitle="Optional overrides. Blank inherits platform rates. Usage is gated by Stripe card hold at session start."
                >
                  <FormSheetFields>
                    <FormSheetField span={2}>
                      <CompanyRateFieldsSection
                        register={form.register}
                        platformSettings={settingsQuery.data}
                        embedded
                      />
                    </FormSheetField>
                  </FormSheetFields>
                </FormSheetSection>
              </div>

              {serverError ? <FormSheetAlert>{serverError}</FormSheetAlert> : null}
            </FormSheetBody>

            <FormSheetFooter>
              <FormSheetFooterCell>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  disabled={createCompany.isPending}
                  className={formSheetActionClass}
                >
                  Cancel
                </Button>
              </FormSheetFooterCell>
              <FormSheetFooterCell>
                <Button
                  type="submit"
                  disabled={createCompany.isPending}
                  className={cn(
                    formSheetActionClass,
                    "bg-primary text-primary-foreground hover:bg-primary/90",
                  )}
                >
                  {createCompany.isPending ? (
                    <>
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    "Create company"
                  )}
                </Button>
              </FormSheetFooterCell>
            </FormSheetFooter>
          </FormSheetFrame>
        </form>
      </FormSheetContent>
    </FormSheet>
  );
}

type CreateCompanyTab = "profile" | "address" | "rates";

// Same ids, labels and icons as Edit company's tabs (DES 01-10-2026: form standard).
const CREATE_COMPANY_TABS: FormSheetTab<CreateCompanyTab>[] = [
  { id: "profile", label: "Profile", icon: Building2 },
  { id: "address", label: "Address", icon: MapPin },
  { id: "rates", label: "Rates", icon: BadgePoundSterling },
];
